<script setup lang="ts">
import { ref } from "vue"
import { storeToRefs } from "pinia"
import { useEpciCommuneStore, useNotifyStore, useOnlineStore, useUserStore } from "~/store"
import { useNuxtApp } from "#app"

const show = defineModel<boolean>("show", { required: true })
const emit = defineEmits<{ imported: [] }>()

const { $api, $permissionCheck } = useNuxtApp()
const { currentEpciCommune } = storeToRefs(useEpciCommuneStore())
const { currentUser } = storeToRefs(useUserStore())

const csv = ref<File | null>(null)
const importing = ref(false)
const isActive = ref(false)

const epciCommuneId = currentEpciCommune.value?.id || null

async function importCsv() {
    if (!csv.value)
        return

    importing.value = true
    const data = new FormData()
    data.append("import", csv.value)
    data.append("epci_commune_id", epciCommuneId)

    try {
        const r = await $api.setRequest({
            url: `import`,
            method: "POST",
            body: data,
        })

        // Show toast
        useNotifyStore().showMessage({
            color: "success",
            title: "Import CSV OK",
            text: r.message,
        })

        isActive.value = false
        // restart prepareoffline
        useOnlineStore().prepareOffline("archive-records", "dossiers")

        emit("imported")
        show.value = false
    }
    catch (error) {
        console.error("Erreur lors de l'import :", error)
    }
    finally {
        importing.value = false
    }
}

function canUpdateDossiers() {
    return currentUser && currentUser.role === "admin" && currentEpciCommune?.mission && $permissionCheck("dossier_export")
}
</script>

<template>
    <v-dialog v-model="show" max-width="500">
        <v-card
            v-if="currentEpciCommune && currentEpciCommune.id && (currentEpciCommune.inventory || currentEpciCommune.download)"
            rounded="lg"
            elevation="10"
        >
            <v-card-title class="d-flex align-center border-b px-6 py-4">
                <h2 class="text-h6 font-weight-bold mb-0">
                    {{ canUpdateDossiers ? 'Import / Export' : 'Export' }}
                </h2>
                <v-spacer />
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    color="grey-darken-1"
                    aria-label="Fermer"
                    @click.prevent="show = false"
                />
            </v-card-title>

            <v-card-text class="px-6 py-5">
                <div v-if="$permissionCheck('dossier_export')" class="mb-6">
                    <p class="text-subtitle-2 text-medium-emphasis mb-3">
                        Télécharger les données actuelles CSV
                    </p>
                    <DocumentsDownloadLink :url="currentEpciCommune.export" :filename="`${currentEpciCommune.v}.csv`">
                        <template #default="{ loading }">
                            <v-btn
                                color="secondary"
                                variant="flat"
                                :loading="loading"
                                block
                                prepend-icon="mdi-export"
                            >
                                Exporter les dossiers
                            </v-btn>
                        </template>
                    </DocumentsDownloadLink>
                </div>

                <v-divider
                    v-if="canUpdateDossiers"
                    class="mb-6"
                />

                <div v-if="canUpdateDossiers">
                    <p class="text-subtitle-2 text-medium-emphasis mb-3">
                        Mettre à jour via un fichier CSV
                    </p>

                    <v-file-input
                        v-model="csv"
                        label="Sélectionner un fichier"
                        accept="text/csv"
                        variant="outlined"
                        density="comfortable"
                        prepend-icon=""
                        prepend-inner-icon="mdi-paperclip"
                        color="primary"
                        class="mb-2"
                        hide-details
                    />

                    <v-btn
                        color="primary"
                        variant="flat"
                        :disabled="!csv"
                        :loading="importing"
                        block
                        prepend-icon="mdi-upload"
                        class="mt-4"
                        @click="isActive = true"
                    >
                        Importer le fichier
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
    <v-dialog v-model="isActive" max-width="450">
        <template #default>
            <v-card>
                <v-card-title class="border-b">
                    Confirmation de l'import CSV
                </v-card-title>
                <v-card-text>
                    <p>Attention, tous les dossiers existants seront supprimés avant import. Veuillez confirmer cette action.</p>
                    <div class="d-flex pt-2">
                        <v-btn
                            text="Annuler"
                            @click="isActive = false"
                        />
                        <v-spacer />
                        <v-btn
                            color="primary"
                            :loading="importing"
                            @click="importCsv"
                        >
                            Importer
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </template>
    </v-dialog>
</template>
